import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { patchJson, postJson } from '@/utils/fetchOptions';
import { useTranslation } from 'react-i18next';

import { useAuthToken, useAuthUser, useMutation } from '..';

interface UseInviteAcceptProps {
  chosenPublishDates: {
    beat_id: string;
    date_publish: Date;
  }[];
  collaborationId: string;
  onSuccess: TypeAny;
  shippingAddress: TypeAny;
}

export function useInviteAccept({
  chosenPublishDates,
  collaborationId,
  onSuccess,
  shippingAddress
}: UseInviteAcceptProps) {
  const { setAlert } = useAppContext();
  const token = useAuthToken();
  const user = useAuthUser();
  const { t } = useTranslation();

  const { isLoading, mutate, error, data } = useMutation({
    mutationKey: `influencer-invite-accept-${collaborationId}`,
    mutationFn: async () => {
      if (!token) throw new Error(t('error.noAuthToken'));

      const { phone, ...rest } = shippingAddress || {};

      const acceptResp = await postJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/influencer/invite/${collaborationId}/accept`,
        body: {
          chosen_dates_publish: chosenPublishDates,
          shipping_address: rest
        },
        token
      });

      if ((acceptResp as ResponseExpiredToken)?.isExpired) {
        logout();
        return acceptResp;
      }
      if ('code' in (acceptResp as TypeAny)) {
        setAlert((acceptResp as ResponseObject).code);
      }

      await patchJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/user/update`,
        body: { phone, shipping_address: rest },
        token
      });

      try {
        localStorage.setItem(
          'auth',
          JSON.stringify({
            token,
            user: {
              ...user,
              country: shippingAddress?.country || user?.country,
              influencer: {
                ...user?.influencer,
                shipping_address: {
                  ...user?.influencer?.shipping_address,
                  ...shippingAddress
                }
              }
            }
          })
        );
      } catch {
        // ignore
      }

      return acceptResp as Api.UserObject & ResponseObject;
    },
    onSuccess
  });

  return {
    data: data || {},
    isLoading,
    mutate,
    error
  };
}
